/**
 * WTR Binary Race Data Decoder
 *
 * Decodes binary race data format for fast loading in the browser.
 * See binaryFormat.md for specification.
 */

const MAGIC = 'WTR1';
const TEAM_COLORS = [
  '#0e4d68', '#03b3fd', '#EFA00B', '#e6332a', '#3a9d7c',
  '#22c55e', '#06b6d4', '#eab308', '#ef4444', '#8b5cf6',
  '#14b8a6', '#0ea5e9', '#f59e0b', '#f97316', '#d946ef',
  '#10b981', '#3b82f6', '#fbbf24', '#ec4899', '#6366f1',
  '#34d399', '#60a5fa', '#fcd34d', '#fb7185', '#a78bfa',
  '#2dd4bf', '#38bdf8', '#fde047', '#f472b6', '#818cf8',
];

/**
 * Decode binary race data into team objects
 * @param {ArrayBuffer} buffer - The binary data
 * @returns {Object} - { teams: Team[], timeRange: { start: Date, end: Date } }
 */
export function decodeRaceData(buffer) {
  const view = new DataView(buffer);
  const decoder = new TextDecoder('utf-8');
  let offset = 0;

  // Read and validate magic number
  const magic = String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3)
  );
  offset += 4;

  if (magic !== MAGIC) {
    throw new Error(`Invalid file format: expected ${MAGIC}, got ${magic}`);
  }

  // Read version
  const version = view.getUint8(offset);
  offset += 1;

  if (version !== 1) {
    throw new Error(`Unsupported format version: ${version}`);
  }

  // Read team count
  const teamCount = view.getUint16(offset, true);
  offset += 2;

  // Read base timestamp (64-bit as two 32-bit values)
  const low = view.getUint32(offset, true);
  offset += 4;
  const high = view.getUint32(offset, true);
  offset += 4;
  const baseTimestamp = low + high * 0x100000000;

  // Track time range
  let minTime = Infinity;
  let maxTime = -Infinity;

  // Read teams
  const teams = [];

  for (let i = 0; i < teamCount; i++) {
    // Read serial
    const serialLen = view.getUint8(offset);
    offset += 1;
    const serial = decoder.decode(new Uint8Array(buffer, offset, serialLen));
    offset += serialLen;

    // Read name
    const nameLen = view.getUint8(offset);
    offset += 1;
    const name = decoder.decode(new Uint8Array(buffer, offset, nameLen));
    offset += nameLen;

    // Read color index
    const colorIndex = view.getUint8(offset);
    offset += 1;

    // Read position count
    const positionCount = view.getUint16(offset, true);
    offset += 2;

    // Read positions
    const positions = new Array(positionCount);

    for (let j = 0; j < positionCount; j++) {
      const lat = view.getFloat32(offset, true);
      offset += 4;

      const lng = view.getFloat32(offset, true);
      offset += 4;

      const speed = view.getUint16(offset, true) / 100;
      offset += 2;

      const dtf = view.getUint16(offset, true) / 10;
      offset += 2;

      const cog = view.getUint16(offset, true) / 10;
      offset += 2;

      const batteryRaw = view.getUint8(offset);
      offset += 1;
      const battery = batteryRaw === 255 ? null : batteryRaw;

      const timeDelta = view.getUint32(offset, true);
      offset += 4;

      const timestamp = new Date(baseTimestamp + timeDelta * 1000);
      const timeMs = timestamp.getTime();

      if (timeMs < minTime) minTime = timeMs;
      if (timeMs > maxTime) maxTime = timeMs;

      positions[j] = {
        lat,
        lng,
        speed,
        speedKmh: speed * 1.852,
        dtf,
        dtfKm: dtf * 1.852,
        cog,
        battery,
        timestamp,
      };
    }

    const latest = positions[positions.length - 1];

    teams.push({
      serial,
      name,
      color: TEAM_COLORS[colorIndex % TEAM_COLORS.length],
      positions,
      latest,
      stats: calculateTeamStats(positions),
    });
  }

  // Sort by distance to finish (finished teams sorted by finish time)
  teams.sort((a, b) => {
    const aDtf = a.latest?.dtf ?? Infinity;
    const bDtf = b.latest?.dtf ?? Infinity;

    // Both finished: sort by finish time (earlier = better)
    if (aDtf === 0 && bDtf === 0) {
      const aTime = a.latest?.timestamp?.getTime() ?? 0;
      const bTime = b.latest?.timestamp?.getTime() ?? 0;
      return aTime - bTime;
    }

    // One finished, one racing: finished comes first
    if (aDtf === 0) return -1;
    if (bDtf === 0) return 1;

    // Both racing: sort by DTF
    return aDtf - bDtf;
  });

  return {
    teams,
    timeRange: {
      start: new Date(minTime),
      end: new Date(maxTime),
    },
  };
}

/**
 * Calculate team statistics
 */
function calculateTeamStats(positions) {
  if (positions.length === 0) return {};

  const speeds = positions.map(p => p.speed).filter(s => s > 0);
  const avgSpeed = speeds.length > 0
    ? speeds.reduce((a, b) => a + b, 0) / speeds.length
    : 0;

  const maxSpeed = Math.max(...speeds, 0);

  // Calculate total distance via haversine
  let totalDistance = 0;
  for (let i = 1; i < positions.length; i++) {
    totalDistance += haversineDistance(
      positions[i - 1].lat, positions[i - 1].lng,
      positions[i].lat, positions[i].lng
    );
  }

  return {
    avgSpeed: avgSpeed.toFixed(1),
    maxSpeed: maxSpeed.toFixed(1),
    totalDistance: totalDistance.toFixed(1),
    positionCount: positions.length,
  };
}

/**
 * Haversine distance in nautical miles
 */
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 3440.065;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Fetch and decode binary race data
 * @param {string} url - URL to the .wtr file
 * @returns {Promise<Object>} - Decoded race data
 */
export async function fetchBinaryRaceData(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const buffer = await response.arrayBuffer();
  return decodeRaceData(buffer);
}
